import type { State } from '../State';
import { duration } from '../utils/duration';
import { stickerCampaign } from '../stickers/stickers.ruleset';

// Add promo keys here
export type PromoID =
  | 'guestModeLogin'
  | 'rateAppAndroid'
  | 'rateAppIOS'
  | 'dailyGacha'
  | 'dailyGachaMultiSpin'
  | 'dailyGachaLaunch'
  | 'crossPlayPromo'
  | 'continueCrossPlayPromo'
  | 'progressionPromoForOldDecorationPlayers'
  | 'masterLeaguePuzzleCoins'
  | 'petStarterPack'
  | 'inGameFriendReferralRewardsCollection'
  | 'championsLeagueEntrySeen'
  | 'homeShortcutPopupAtLaunch'
  | 'homeShortcutAdd'
  | 'homeShortcutLaunch'
  | '2025-setsubun-gacha'
  | '2025-valentines-gacha'
  | '2025-cafe-gacha'
  | '2025-hanami-gacha'
  | 'pushNotificationReminderV2'
  | 'updateAppReminder24h'
  | 'updateAppReminder72h'
  | '2025-childrens-day-gacha'
  | '2025-mothers-day-gacha'
  | '2025-melon-season-gacha'
  | '2025-tanabata-gacha'
  | '2025-kakigori-gacha'
  | '2025-moonviewing2025-gacha'
  | '2025-magical-books-gacha'
  | '2025-taste-autumn-gacha'
  | '2025-winter-candle-gacha'
  | '2025-ice-queens-castle-gacha'
  | '2026-artisan-chocolatier-gacha'
  | '2026-valentine-day-gacha'
  | '2026-spring-tea-party-gacha'
  | '2026-easter-egg-hunt-gacha'
  | '2026-golden-week-gacha'
  | '2026-rainyseason-bookcafe-gacha'
  | '2026-tropical-island-vacation-gacha'
  | '2026-seashell-gacha'
  | '2026-early-autumn-picnic-gacha'
  | '2026-pumpkin-farm-gacha'
  | 'jankenEventPromo'
  | 'dreamDriftEventPromo'
  /** @deprecated A/B 0494 resolved to Control; retained for stored promo history. */
  | 'incentivizedOAIntroduction'
  /** @deprecated A/B 0494 resolved to Control; retained for stored promo history. */
  | 'incentivizedOAReminder'
  /** @deprecated A/B 0494 resolved to Control; retained for stored promo history. */
  | 'incentivizedOAPostTutorial'
  | 'sponsoredStickers2026';

// Alias for record
export type Promo = Record<PromoID, PromoItemConfig>;

// Config for one promo record
export type PromoItemConfig = {
  // How much time should pass before the promo can be shown again.
  // An array of numbers can be used to specify cooldowns depending on how many times the promo has been shown.
  cooldown: number | readonly number[]; // duration({ hours: 23 })
  // Number of times the promo can be claimed during the cooldown period (default 1)
  limit?: number;
  // UTC time when promo should start
  start?: number; // new Date("2022-11-25T22:15:00.000+09:00").getTime(); - Use for JST dates
  // UTC time when promo should end
  end?: number; // new Date("2022-11-25T22:15:00.000+09:00").getTime();
  // Flag to indicate that rewards are claimable each time the promo is shown (default false)
  isRepeatable?: boolean;
  // Reward for promo. Return undefined if no reward.
  getRewards?: (
    state: State,
    now: number,
  ) =>
    | {
        // Gems
        gems?: number;
        // Clears all pet status effects, and set pets attributes to 100%.
        makePetHappy?: boolean;
      }
    | undefined;
};

// App ratings, iOS and Android:
const rateAppAndroid: PromoItemConfig = { cooldown: duration({ days: 30 }) };
const rateAppIOS: PromoItemConfig = { cooldown: duration({ days: 30 }) };

// Daily gacha promo, for a single spin only
const dailyGacha: PromoItemConfig = { cooldown: duration({ days: 1 }) };

// Daily gacha multi (10x) spin promo.
const dailyGachaMultiSpin: PromoItemConfig = {
  cooldown: duration({ days: 1 }),
};

// Daily gacha promo launch.
const dailyGachaLaunch: PromoItemConfig = { cooldown: duration({ hours: 1 }) };

const setsubun: PromoItemConfig = {
  start: new Date('2025-01-31T21:00:00.000+09:00').getTime(),
  end: new Date('2025-02-08T22:59:00.000+09:00').getTime(),
  cooldown: duration({ days: 9 }),
};

const valentines: PromoItemConfig = {
  start: new Date('2025-02-14T12:00:00.000+09:00').getTime(),
  end: new Date('2025-02-17T12:00:00.000+09:00').getTime(),
  cooldown: duration({ days: 3 }),
};

const cafe: PromoItemConfig = {
  start: new Date('2025-02-28T21:00:00.000+09:00').getTime(),
  end: new Date('2025-03-08T22:59:00.000+09:00').getTime(),
  cooldown: duration({ days: 9 }),
};

const hanami: PromoItemConfig = {
  start: new Date('2025-03-31T21:00:00.000+09:00').getTime(),
  end: new Date('2025-04-08T22:59:00.000+09:00').getTime(),
  cooldown: duration({ days: 9 }),
};

const childrens: PromoItemConfig = {
  start: new Date('2025-04-30T21:00:00.000+09:00').getTime(),
  end: new Date('2025-05-08T22:59:00.000+09:00').getTime(),
  cooldown: duration({ days: 9 }),
};

const mothersday: PromoItemConfig = {
  start: new Date('2025-05-09T00:00:00.000+09:00').getTime(),
  end: new Date('2025-05-12T23:59:00.000+09:00').getTime(),
  cooldown: duration({ days: 3 }),
};

const melonseason: PromoItemConfig = {
  start: new Date('2025-05-31T21:00:00.000+09:00').getTime(),
  end: new Date('2025-06-08T22:59:00.000+09:00').getTime(),
  cooldown: duration({ days: 9 }),
};

const tanabata: PromoItemConfig = {
  start: new Date('2025-06-30T21:00:00.000+09:00').getTime(),
  end: new Date('2025-07-08T22:59:00.000+09:00').getTime(),
  cooldown: duration({ days: 9 }),
};

const kakigori: PromoItemConfig = {
  start: new Date('2025-07-31T21:00:00.000+09:00').getTime(),
  end: new Date('2025-08-08T22:59:00.000+09:00').getTime(),
  cooldown: duration({ days: 9 }),
};

const moonviewing2025: PromoItemConfig = {
  start: new Date('2025-08-31T21:00:00.000+09:00').getTime(),
  end: new Date('2025-09-08T22:59:00.000+09:00').getTime(),
  cooldown: duration({ days: 9 }),
};

const magicalBooks: PromoItemConfig = {
  start: new Date('2025-09-30T21:00:00.000+09:00').getTime(),
  end: new Date('2025-10-08T22:59:00.000+09:00').getTime(),
  cooldown: duration({ days: 9 }),
};

const tasteAutumn: PromoItemConfig = {
  start: new Date('2025-10-31T21:00:00.000+09:00').getTime(),
  end: new Date('2025-11-08T22:59:00.000+09:00').getTime(),
  cooldown: duration({ days: 9 }),
};

const winterCandle: PromoItemConfig = {
  start: new Date('2025-11-30T21:00:00.000+09:00').getTime(),
  end: new Date('2025-12-08T22:59:00.000+09:00').getTime(),
  cooldown: duration({ days: 9 }),
};

const iceQueensCastle: PromoItemConfig = {
  start: new Date('2025-12-31T21:00:00.000+09:00').getTime(),
  end: new Date('2026-01-08T22:59:00.000+09:00').getTime(),
  cooldown: duration({ days: 9 }),
};

const artisanChocolatier: PromoItemConfig = {
  start: new Date('2026-01-31T21:00:00.000+09:00').getTime(),
  end: new Date('2026-02-08T22:59:00.000+09:00').getTime(),
  cooldown: duration({ days: 9 }),
};

const valentineDay: PromoItemConfig = {
  start: new Date('2026-02-12T21:00:00.000+09:00').getTime(),
  end: new Date('2026-02-15T22:59:00.000+09:00').getTime(),
  cooldown: duration({ days: 4 }),
};

const springTeaParty: PromoItemConfig = {
  start: new Date('2026-02-28T21:00:00.000+09:00').getTime(),
  end: new Date('2026-03-08T22:59:00.000+09:00').getTime(),
  cooldown: duration({ days: 9 }),
};

const easterEggHunt: PromoItemConfig = {
  start: new Date('2026-03-31T21:00:00.000+09:00').getTime(),
  end: new Date('2026-04-08T22:59:00.000+09:00').getTime(),
  cooldown: duration({ days: 9 }),
};

const goldenWeek: PromoItemConfig = {
  start: new Date('2026-04-30T21:00:00.000+09:00').getTime(),
  end: new Date('2026-05-08T22:59:00.000+09:00').getTime(),
  cooldown: duration({ days: 9 }),
};

const rainySeasonBookCafe: PromoItemConfig = {
  start: new Date('2026-05-30T21:00:00.000+09:00').getTime(),
  end: new Date('2026-06-08T22:59:00.000+09:00').getTime(),
  cooldown: duration({ days: 9 }),
};

const tropicalIslandVacation: PromoItemConfig = {
  start: new Date('2026-06-30T21:00:00.000+09:00').getTime(),
  end: new Date('2026-07-08T22:59:00.000+09:00').getTime(),
  cooldown: duration({ days: 9 }),
};

/** Promo timing for the Seashell gacha event. */
const seashell: PromoItemConfig = {
  start: new Date('2026-07-31T21:00:00.000+09:00').getTime(),
  end: new Date('2026-08-08T22:59:00.000+09:00').getTime(),
  cooldown: duration({ days: 9 }),
};

/** Promo timing for the Early Autumn Picnic gacha event. */
const earlyAutumnPicnic: PromoItemConfig = {
  start: new Date('2026-08-31T21:00:00.000+09:00').getTime(),
  end: new Date('2026-09-08T22:59:00.000+09:00').getTime(),
  cooldown: duration({ days: 9 }),
};

/** Promo timing for the Pumpkin Farm gacha event. */
const pumpkinFarm: PromoItemConfig = {
  start: new Date('2026-09-30T21:00:00.000+09:00').getTime(),
  end: new Date('2026-10-08T22:59:00.000+09:00').getTime(),
  cooldown: duration({ days: 9 }),
};

// Main promo config
export const config: Promo = {
  rateAppAndroid,
  rateAppIOS,
  dailyGacha,
  dailyGachaMultiSpin,
  dailyGachaLaunch,
  crossPlayPromo: { cooldown: duration({ hours: 3 }) },
  continueCrossPlayPromo: { cooldown: duration({ hours: 1 }) },
  progressionPromoForOldDecorationPlayers: {
    cooldown: duration({ hours: 72 }),
  },
  masterLeaguePuzzleCoins: { cooldown: duration({ days: 9999 }) },
  petStarterPack: { cooldown: duration({ days: 9999 }) },
  inGameFriendReferralRewardsCollection: { cooldown: duration({ hours: 23 }) },
  championsLeagueEntrySeen: { cooldown: duration({ days: 9999 }) },
  homeShortcutPopupAtLaunch: { cooldown: duration({ hours: 23 }) },
  homeShortcutAdd: {
    cooldown: duration({ days: 9999 }),
  },
  homeShortcutLaunch: {
    cooldown: duration({ days: 9999 }),
    getRewards: () => ({ gems: 200 }),
  },
  '2025-setsubun-gacha': setsubun,
  '2025-valentines-gacha': valentines,
  '2025-cafe-gacha': cafe,
  '2025-hanami-gacha': hanami,
  '2025-childrens-day-gacha': childrens,
  '2025-mothers-day-gacha': mothersday,
  '2025-melon-season-gacha': melonseason,
  '2025-tanabata-gacha': tanabata,
  '2025-kakigori-gacha': kakigori,
  '2025-moonviewing2025-gacha': moonviewing2025,
  '2025-magical-books-gacha': magicalBooks,
  '2025-taste-autumn-gacha': tasteAutumn,
  '2025-winter-candle-gacha': winterCandle,
  '2025-ice-queens-castle-gacha': iceQueensCastle,
  '2026-artisan-chocolatier-gacha': artisanChocolatier,
  '2026-valentine-day-gacha': valentineDay,
  '2026-spring-tea-party-gacha': springTeaParty,
  '2026-easter-egg-hunt-gacha': easterEggHunt,
  '2026-golden-week-gacha': goldenWeek,
  '2026-rainyseason-bookcafe-gacha': rainySeasonBookCafe,
  '2026-tropical-island-vacation-gacha': tropicalIslandVacation,
  '2026-seashell-gacha': seashell,
  '2026-early-autumn-picnic-gacha': earlyAutumnPicnic,
  '2026-pumpkin-farm-gacha': pumpkinFarm,
  guestModeLogin: { cooldown: duration({ days: 1 }) },
  pushNotificationReminderV2: { cooldown: duration({ days: 9999 }) },
  updateAppReminder24h: { cooldown: duration({ days: 1 }) },
  updateAppReminder72h: { cooldown: duration({ days: 3 }) },
  jankenEventPromo: { cooldown: duration({ days: 4 }) },
  dreamDriftEventPromo: { cooldown: duration({ days: 4 }) },
  // Retain retired A/B 0494 promo registrations for existing player state.
  incentivizedOAIntroduction: { cooldown: duration({ days: 9999 }) },
  incentivizedOAReminder: { cooldown: duration({ days: 9999 }) },
  incentivizedOAPostTutorial: { cooldown: duration({ days: 9999 }) },
  // Record every display, including repeat payload entries. The sticker getter
  // applies fixed campaign-week boundaries only to normal-launch eligibility.
  sponsoredStickers2026: {
    start: Date.parse(stickerCampaign.start),
    // Promo end dates are inclusive; the schedule's end is exclusive.
    end: Date.parse(stickerCampaign.end) - 1,
    cooldown: 0,
  },
} as const;
